import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback } from 'react';
import { colorTokenFromNestedArguments, DesignContextOverridesProvider, DesignContextForwardProvider, referenceForwardArgs, useDesignContext } from '@klarna/designtokens';
import { View } from '@bubble-ui/primitive-view';
import { usePropsFromContext } from '@bubble-ui/provider-props-context';
import { TouchableControlProvider } from '@bubble-ui/provider-touchable-control';
import { useInteractiveStateCombined } from '@bubble-ui/hook-use-interactive-state';
import { Touchable } from '@bubble-ui/primitive-touchable';
import { isWeb } from '@bubble-ui/util-platform';
import { ItemInformation } from '@bubble-ui/molecule-item-information';
import { usePropDeprecationWarning } from '@bubble-ui/hook-use-prop-deprecation-warning';
import { useTokenDeprecationWarning } from '@bubble-ui/hook-use-token-deprecation-warning';
import { defaultComponentTokens } from './defaultComponentTokens';
import { getDefaultStyleSheet } from './getDefaultStyleSheet';
var ItemTouchable = _ref => {
  var {
    'aria-checked': ariaChecked,
    'aria-label': ariaLabel,
    borderColorToken,
    borderColorTokenArgs,
    children,
    description,
    disabled,
    focused,
    framed,
    hasBorder,
    horizontalPaddingToken,
    hovered,
    href,
    id,
    keyboardFocused,
    minHeight,
    name,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPress,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed,
    rel,
    role = 'button',
    tabIndex,
    target,
    title,
    UNSAFE_containerProps,
    UNSAFE_touchableProps,
    UNSAFE_style,
    value,
    verticalPaddingToken
  } = _ref;
  usePropDeprecationWarning({
    componentName: 'ItemTouchable',
    propName: 'hasBorder',
    shouldWarn: hasBorder !== undefined,
    useInstead: 'borderColorToken="colors/borders/subtle-floating"',
    removalDate: 'v8.0.0'
  });
  var designContext = useDesignContext(defaultComponentTokens);
  var deprecatedColorTokenExists = designContext.hasTokenInContext('item-touchable/border-color');
  useTokenDeprecationWarning({
    componentName: 'ItemTouchable',
    tokenName: 'item-touchable/border-color',
    shouldWarn: deprecatedColorTokenExists,
    removalDate: 'v8.0.0'
  });
  var propsFromLayoutContext = usePropsFromContext(ItemTouchable.displayName);
  var [interactiveStateCombined, eventHandlers] = useInteractiveStateCombined({
    disabled,
    focused,
    hovered,
    keyboardFocused,
    onBlur,
    onFocus,
    onMouseDown,
    onMouseOut,
    onMouseOver,
    onMouseUp,
    onPressIn,
    onPressOut,
    onTouchEnd,
    onTouchStart,
    pressed
  });
  var isFramedFinal = framed !== undefined ? framed : propsFromLayoutContext.framed !== undefined ? propsFromLayoutContext.framed : designContext.get('item-touchable/framed');
  var {
    touchableStyle,
    keyboardFocusedStyle,
    inputStyle,
    labelStyle,
    anchorStyle
  } = getDefaultStyleSheet({
    designContext,
    disabled: interactiveStateCombined.disabled,
    framed: isFramedFinal,
    keyboardFocused: interactiveStateCombined.keyboardFocused,
    keyboardFocusStyle: propsFromLayoutContext.keyboardFocusStyle
  });

  // copied straight from RadioGroup
  var handleOnChangeForRadio = useCallback(e => {
    // @ts-expect-error
    onPress(value, e);
  }, [onPress, value]);
  var handleOnChangeForCheckboxOrSwitch = useCallback(e => {
    // @ts-expect-error
    onPress(!ariaChecked, e);
  }, [onPress, ariaChecked]);
  var ItemTouchableContent = /*#__PURE__*/React.createElement(TouchableControlProvider, {
    turnOffTouchability: true
  }, /*#__PURE__*/React.createElement(ItemInformation, {
    borderColorToken: borderColorToken || (hasBorder ? deprecatedColorTokenExists ? 'item-touchable/border-color' : 'colors/borders/subtle-floating' : undefined),
    borderColorTokenArgs: _objectSpread({
      interactiveState: interactiveStateCombined.interactiveState
    }, borderColorTokenArgs),
    description: description,
    disabled: interactiveStateCombined.disabled,
    framed: framed,
    horizontalPaddingToken: horizontalPaddingToken,
    minHeight: minHeight,
    title: title,
    verticalPaddingToken: verticalPaddingToken,
    UNSAFE_containerProps: UNSAFE_containerProps,
    UNSAFE_style: UNSAFE_style
  }, children));
  return /*#__PURE__*/React.createElement(DesignContextForwardProvider, {
    context: designContext
  }, /*#__PURE__*/React.createElement(DesignContextOverridesProvider, {
    tokens: {
      'item-information/corner-radius': referenceForwardArgs('item-touchable/corner-radius'),
      'item-information/border-width': referenceForwardArgs('item-touchable/border-width'),
      'item-information/background-color': colorTokenFromNestedArguments({
        framed: () => designContext.get('item-touchable/background-color', {
          framingVariant: 'framed',
          interactiveState: interactiveStateCombined.disabled ? 'default' : interactiveStateCombined.interactiveState
        }),
        default: () => designContext.get('item-touchable/background-color', {
          framingVariant: 'default',
          interactiveState: interactiveStateCombined.disabled ? 'default' : interactiveStateCombined.interactiveState
        })
      }, ['framingVariant']),
      'item-information/title-text-color': referenceForwardArgs('item-touchable/title-text-color'),
      'item-information/title-text-style': referenceForwardArgs('item-touchable/title-text-style'),
      'item-information/description-text-color': referenceForwardArgs('item-touchable/description-text-color'),
      'item-information/description-text-style': referenceForwardArgs('item-touchable/description-text-style'),
      'item-information/slot-right-text-color': referenceForwardArgs('item-touchable/slot-right-text-color'),
      'item-information/slot-right-text-style': referenceForwardArgs('item-touchable/slot-right-text-style'),
      'item-information/slot-left-text-color': referenceForwardArgs('item-touchable/slot-left-text-color'),
      'item-information/slot-left-text-style': referenceForwardArgs('item-touchable/slot-left-text-style'),
      'item-information/slot-bottom-text-style': referenceForwardArgs('item-touchable/slot-bottom-text-style'),
      'item-information/slot-bottom-text-color': referenceForwardArgs('item-touchable/slot-bottom-text-color'),
      'item-information/slot-middle-text-style': referenceForwardArgs('item-touchable/slot-middle-text-style'),
      'item-information/slot-middle-text-color': referenceForwardArgs('item-touchable/slot-middle-text-color'),
      'item-information/side-padding': referenceForwardArgs(horizontalPaddingToken || 'item-touchable/side-padding'),
      'item-information/vertical-padding': referenceForwardArgs(verticalPaddingToken || 'item-touchable/vertical-padding'),
      'item-information/framed': referenceForwardArgs('item-touchable/framed')
    }
  }, isWeb && ['radio', 'checkbox'].includes(role) ? /*#__PURE__*/React.createElement("label", _extends({
    htmlFor: id
  }, eventHandlers, {
    style: labelStyle
  }), /*#__PURE__*/React.createElement("input", _extends({
    id: id
  }, UNSAFE_touchableProps, {
    "aria-checked": ariaChecked,
    "aria-disabled": interactiveStateCombined.disabled,
    "aria-label": ariaLabel,
    style: inputStyle,
    name: name,
    type: role,
    checked: ariaChecked,
    disabled: interactiveStateCombined.disabled,
    onChange: role === 'radio' ? handleOnChangeForRadio : handleOnChangeForCheckboxOrSwitch,
    tabIndex: tabIndex,
    value: value
  })), interactiveStateCombined.keyboardFocused && /*#__PURE__*/React.createElement(View, {
    style: keyboardFocusedStyle
  }), ItemTouchableContent) : isWeb && role === 'link' ? /*#__PURE__*/React.createElement("a", _extends({
    id: id
  }, UNSAFE_touchableProps, interactiveStateCombined.disabled ? {
    'aria-disabled': true
  } : {
    href,
    onClick: onPress,
    rel,
    target
  }, {
    role: role,
    tabIndex: tabIndex || 0,
    style: anchorStyle
  }, eventHandlers), interactiveStateCombined.keyboardFocused && /*#__PURE__*/React.createElement(View, {
    style: keyboardFocusedStyle
  }), ItemTouchableContent) : /*#__PURE__*/React.createElement(Touchable, _extends({
    id: id
  }, UNSAFE_touchableProps, {
    accessible: true,
    "aria-disabled": interactiveStateCombined.disabled,
    "aria-label": ariaLabel,
    role: role,
    accessibilityRole: role,
    "aria-checked": ariaChecked,
    style: touchableStyle
  }, interactiveStateCombined.disabled ? {} : {
    onPress: role === 'radio' ? handleOnChangeForRadio : ['checkbox', 'switch'].includes(role) ? handleOnChangeForCheckboxOrSwitch : onPress
  }, {
    disabled: interactiveStateCombined.disabled,
    tabIndex: tabIndex,
    type: "button",
    value: value,
    name: name
  }, eventHandlers), /*#__PURE__*/React.createElement(View, null, interactiveStateCombined.keyboardFocused && /*#__PURE__*/React.createElement(View, {
    style: keyboardFocusedStyle
  }), ItemTouchableContent))));
};
ItemTouchable.displayName = 'ItemTouchable';
var SlotRight = props => /*#__PURE__*/React.createElement(ItemInformation.SlotRight, props);
var SlotLeft = props => /*#__PURE__*/React.createElement(ItemInformation.SlotLeft, props);
var SlotBottom = props => /*#__PURE__*/React.createElement(ItemInformation.SlotBottom, props);
var SlotMiddle = props => /*#__PURE__*/React.createElement(ItemInformation.SlotMiddle, props);
SlotRight.displayName = 'ItemTouchable.SlotRight';
SlotLeft.displayName = 'ItemTouchable.SlotLeft';
SlotBottom.displayName = 'ItemTouchable.SlotBottom';
SlotMiddle.displayName = 'ItemTouchable.SlotMiddle';
ItemTouchable.SlotRight = SlotRight;
ItemTouchable.SlotLeft = SlotLeft;
ItemTouchable.SlotBottom = SlotBottom;
ItemTouchable.SlotMiddle = SlotMiddle;
export { ItemTouchable };
//# sourceMappingURL=index.js.map